import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import '@cyntox/common/styles.css';
import { Gate } from '@cyntox/common/platform';
import { initAuth } from './api/auth';
import { AppProvider, useApp } from './state/AppContext';
import { Shell } from './components/Shell';
import { ThisWeek } from './screens/ThisWeek';
import { Counts } from './screens/Counts';
import { CountEntry } from './screens/CountEntry';
import { Locations } from './screens/Locations';
import { Equipment } from './screens/Equipment';
import { Targets } from './screens/Targets';
import { Help } from './screens/Help';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Sends each role to the first Inventory screen it can actually see. */
function Landing() {
  const { can } = useApp();
  if (can('counts', 'view')) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/setup/locations" replace />;
}

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppProvider>
            <Routes>
              <Route element={<Shell />}>
                <Route index element={<Landing />} />
                {/* Each screen checks its capability at the route, so a typed URL
                    without access says so instead of showing an empty list. */}
                <Route path="/dashboard" element={<Gate any={[['counts', 'view']]}><ThisWeek /></Gate>} />
                {/* The dashboard's first address, kept so an old link still lands. */}
                <Route path="/week" element={<Navigate to="/dashboard" replace />} />
                <Route path="/counts" element={<Gate any={[['counts', 'view']]}><Counts /></Gate>} />
                <Route path="/counts/:locationId" element={<Gate any={[['counts', 'view']]}><CountEntry /></Gate>} />
                <Route path="/setup/locations" element={<Gate any={[['setup', 'view']]}><Locations /></Gate>} />
                <Route path="/setup/supplies" element={<Gate any={[['setup', 'view']]}><Equipment /></Gate>} />
                {/* The screen's first address, kept so an old link still lands. */}
                <Route path="/setup/equipment" element={<Navigate to="/setup/supplies" replace />} />
                <Route path="/setup/targets" element={<Gate any={[['setup', 'view']]}><Targets /></Gate>} />
                {/* No capability: everyone who can sign in can read the guide. */}
                <Route path="/help" element={<Help />} />
                <Route path="*" element={<Landing />} />
              </Route>
            </Routes>
          </AppProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Nothing renders until we know how to authenticate. With Entra that means a
// token is in hand; without it, the browser has already left for Microsoft and
// this page is being replaced.
initAuth().then(render).catch((err: Error) => {
  document.getElementById('root')!.innerHTML =
    `<div style="font:14px system-ui;padding:40px;max-width:640px">`
    + `<h2 style="margin:0 0 8px">Inventory could not start</h2>`
    + `<p style="color:#5a5852">${err.message}</p>`
    + `<p style="color:#9a9890;font-size:12px">Check that the API is running and reachable.</p>`
    + `</div>`;
});
